class CitiesLoader {
  constructor(config) {
    this.storefrontToken = config.storefrontToken;
    this.shopDomain = config.shopDomain;
    this.cityHandle = config.cityHandle || 'rozvoz-kvetin-cz';
    this.handle = config.handle || 'rozvoz-kvetin-do-ceskej-republiky';
    this.allCitiesCache = null;
    this.isLoading = false;
    
    this.elements = {
      container: document.getElementById('cities-container'),
      loading: document.getElementById('cities-loading'),
      content: document.getElementById('cities-content'),
      grid: document.getElementById('cities-grid')
    };
  }

  getCurrentLetter() {
    const path = window.location.pathname;
    const escapedHandle = this.handle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp(`/pages/${escapedHandle}/([a-z])`, 'i');
    const match = path.match(pattern);
    
    if (match) {
      return match[1].toLowerCase();
    }
    
    const firstLetterLink = document.querySelector('.inline-list a');
    if (firstLetterLink) {
      const href = firstLetterLink.getAttribute('href');
      const letterMatch = href.match(/\/([a-z])$/i);
      return letterMatch ? letterMatch[1].toLowerCase() : 'a';
    }
    
    return 'a';
  }

  getCitiesQuery() {
    return `
      query getCities($first: Int!, $after: String) {
        metaobjects(type: "city", first: $first, after: $after) {
          edges {
            node {
              id
              handle
              fields {
                key
                value
              }
            }
            cursor
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    `;
  }

  async fetchAllCities() {
    if (this.allCitiesCache) {
      return this.allCitiesCache;
    }

    if (this.isLoading) {
      while (this.isLoading) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      return this.allCitiesCache;
    }

    this.isLoading = true;
    const query = this.getCitiesQuery();
    let allCities = [];
    let hasNextPage = true;
    let cursor = null;

    try {
      while (hasNextPage) {
        const variables = {
          first: 250,
          after: cursor,
        };

        const response = await fetch(`https://${this.shopDomain}/api/2023-10/graphql.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.storefrontToken,
          },
          body: JSON.stringify({ query, variables }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.errors) {
          console.error('GraphQL errors:', data.errors);
          throw new Error(data.errors[0].message);
        }

        const batchCities = data.data.metaobjects.edges.map((edge) => {
          const fields = {};
          edge.node.fields.forEach((field) => {
            fields[field.key] = field.value;
          });
          return fields;
        });

        allCities = allCities.concat(batchCities);

        hasNextPage = data.data.metaobjects.pageInfo.hasNextPage;
        cursor = data.data.metaobjects.pageInfo.endCursor;

        console.log(`Fetched ${batchCities.length} cities, total: ${allCities.length}`);
      }

      console.log(`Finished fetching all ${allCities.length} cities`);
      this.allCitiesCache = allCities;
      return allCities;
    } catch (error) {
      console.error('Error fetching cities:', error);
      throw error;
    } finally {
      this.isLoading = false;
    }
  }

  normalizeFirstLetter(text) {
    if (!text) return '';
    const firstChar = text.charAt(0).toLowerCase();
    const normalizedChar = this.transliterateCzech(firstChar);
    return normalizedChar;
  }

  async fetchCities(letter) {
    try {
      const allCities = await this.fetchAllCities();

      const filteredCities = allCities.filter((city) => {
        const cityName = city.city || city.name || '';
        const normalizedFirstLetter = this.normalizeFirstLetter(cityName);
        const normalizedTargetLetter = this.transliterateCzech(letter.toLowerCase());
        return normalizedFirstLetter === normalizedTargetLetter;
      });

      console.log(`Found ${filteredCities.length} cities starting with ${letter.toUpperCase()}`);
      return filteredCities;
    } catch (error) {
      console.error('Error filtering cities:', error);
      throw error;
    }
  }

  transliterateCzech(text) {
    const czechMap = {
      'á': 'a', 'à': 'a', 'â': 'a', 'ä': 'a', 'ã': 'a', 'å': 'a', 'ā': 'a',
      'č': 'c', 'ć': 'c', 'ç': 'c',
      'ď': 'd',
      'é': 'e', 'è': 'e', 'ê': 'e', 'ë': 'e', 'ē': 'e', 'ě': 'e',
      'í': 'i', 'ì': 'i', 'î': 'i', 'ï': 'i', 'ī': 'i',
      'ľ': 'l', 'ĺ': 'l',
      'ň': 'n', 'ń': 'n',
      'ó': 'o', 'ò': 'o', 'ô': 'o', 'ö': 'o', 'õ': 'o', 'ō': 'o',
      'ř': 'r', 'ŕ': 'r',
      'š': 's', 'ś': 's',
      'ť': 't',
      'ú': 'u', 'ù': 'u', 'û': 'u', 'ü': 'u', 'ū': 'u', 'ů': 'u',
      'ý': 'y', 'ỳ': 'y', 'ŷ': 'y', 'ÿ': 'y',
      'ž': 'z', 'ź': 'z',
      'Á': 'A', 'À': 'A', 'Â': 'A', 'Ä': 'A', 'Ã': 'A', 'Å': 'A', 'Ā': 'A',
      'Č': 'C', 'Ć': 'C', 'Ç': 'C',
      'Ď': 'D',
      'É': 'E', 'È': 'E', 'Ê': 'E', 'Ë': 'E', 'Ē': 'E', 'Ě': 'E',
      'Í': 'I', 'Ì': 'I', 'Î': 'I', 'Ï': 'I', 'Ī': 'I',
      'Ľ': 'L', 'Ĺ': 'L',
      'Ň': 'N', 'Ń': 'N',
      'Ó': 'O', 'Ò': 'O', 'Ô': 'O', 'Ö': 'O', 'Õ': 'O', 'Ō': 'O',
      'Ř': 'R', 'Ŕ': 'R',
      'Š': 'S', 'Ś': 'S',
      'Ť': 'T',
      'Ú': 'U', 'Ù': 'U', 'Û': 'U', 'Ü': 'U', 'Ū': 'U', 'Ů': 'U',
      'Ý': 'Y', 'Ỳ': 'Y', 'Ŷ': 'Y', 'Ÿ': 'Y',
      'Ž': 'Z', 'Ź': 'Z'
    };

    return text.replace(/[^\w\s-]/g, (char) => czechMap[char] || char);
  }

  generateCityHandle(cityName) {
    return this.transliterateCzech(cityName)
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  displayCities(cities, letter) {
    const { loading, content, grid } = this.elements;

    loading.style.display = 'none';

    if (cities.length === 0) {
      content.style.display = 'none';
      return;
    }

    grid.innerHTML = '';
    cities.forEach((city) => {
      const li = document.createElement('li');
      li.className = 'city-item';

      const cityName = city.city || city.name || 'Unknown City';
      const cityHandle = this.generateCityHandle(cityName);

      const link = document.createElement('a');
      link.href = `/pages/${this.cityHandle}/${cityHandle}`;
      link.className = 'city-link';
      
      const cityNameSpan = document.createElement('span');
      cityNameSpan.className = 'city-name';
      cityNameSpan.textContent = cityName;
      link.appendChild(cityNameSpan);

      if (city.region) {
        const regionSpan = document.createElement('span');
        regionSpan.className = 'city-region';
        regionSpan.textContent = ` (${city.region})`;
        link.appendChild(regionSpan);
      }

      li.appendChild(link);
      grid.appendChild(li);
    });

    content.style.display = 'block';
  }


  async loadCities() {
    const letter = this.getCurrentLetter();

    if (!this.storefrontToken) {
      return;
    }

    try {
      const cities = await this.fetchCities(letter);
      this.displayCities(cities, letter);
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  }

  init() {
    this.loadCities();

    document.querySelectorAll('.inline-list a').forEach((link) => {
      link.addEventListener('click', (e) => {
        setTimeout(() => this.loadCities(), 100);
      });
    });
  }
}

document.addEventListener('DOMContentLoaded', function () {
  if (window.citiesLoaderConfig) {
    const loader = new CitiesLoader(window.citiesLoaderConfig);
    loader.init();
  }
});
